import React, { useEffect, useState } from "react";
import MarkdownText from "../common/MarkdownText";
import { useGalleryViewer } from "../gallery/GalleryViewerProvider";
import { fetchPublicGalleryAlbum } from "../../utils/publicGalleryApi";
import { fetchPublicPage } from "../../utils/publicPagesApi";

function GalleryBlock({ albumSlug }) {
  const { openAlbum } = useGalleryViewer();
  const [album, setAlbum] = useState(null);
  useEffect(() => {
    let cancel = false;
    fetchPublicGalleryAlbum(albumSlug).then((next) => {
      if (!cancel) setAlbum(next);
    });
    return () => {
      cancel = true;
    };
  }, [albumSlug]);
  if (!album) return null;
  return (
    <section className="mt-8" aria-label={album.title}>
      <h2 className="smym-display text-2xl text-[var(--smym-ink)]">{album.title}</h2>
      {album.description ? (
        <p className="mt-2 text-sm text-[var(--smym-ink-muted)]">{album.description}</p>
      ) : null}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {album.photos.map((photo, index) => (
          <button
            key={photo.id}
            type="button"
            className="overflow-hidden rounded-lg border border-[var(--smym-line)]"
            aria-label={`Open ${photo.alt || album.title}, photo ${index + 1}`}
            onClick={() => openAlbum(album, index)}
          >
            <img
              src={photo.url}
              alt={photo.alt || album.title}
              loading="lazy"
              draggable={false}
              className="aspect-[4/3] h-full w-full object-cover"
            />
          </button>
        ))}
      </div>
    </section>
  );
}

export function PageBlocks({ blocks }) {
  return (
    <div className="smym-page__blocks">
      {(blocks || []).map((block, index) => {
        const key = `${block.type}-${index}`;
        if (block.type === "heading") {
          return (
            <h2 key={key} className="smym-display mt-8 text-3xl text-[var(--smym-ink)]">
              {block.text}
            </h2>
          );
        }
        if (block.type === "text") {
          return (
            <MarkdownText
              key={key}
              className="mt-4 text-base leading-relaxed text-[var(--smym-ink)]"
            >
              {block.markdown}
            </MarkdownText>
          );
        }
        if (block.type === "image") {
          return (
            <figure key={key} className="mt-6">
              <img
                src={block.url}
                alt={block.alt || ""}
                loading="lazy"
                decoding="async"
                className="w-full rounded-lg border border-[var(--smym-line)]"
              />
              {block.caption ? (
                <figcaption className="mt-2 text-sm text-[var(--smym-ink-muted)]">
                  {block.caption}
                </figcaption>
              ) : null}
            </figure>
          );
        }
        if (block.type === "button") {
          const external = /^https:\/\//i.test(block.url);
          const className =
            block.style === "secondary"
              ? "smym-btn smym-btn-outline mt-6"
              : "smym-btn smym-btn-primary mt-6";
          return (
            <p key={key}>
              <a
                href={block.url}
                className={className}
                {...(external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : null)}
              >
                {block.label}
              </a>
            </p>
          );
        }
        if (block.type === "gallery") {
          return <GalleryBlock key={key} albumSlug={block.albumSlug} />;
        }
        if (block.type === "divider") {
          return (
            <hr
              key={key}
              className="my-8 border-0 border-t border-[var(--smym-line)]"
            />
          );
        }
        return null;
      })}
    </div>
  );
}

function usePageMeta(title, description) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    const tag = document.querySelector('meta[name="description"]');
    const previous = tag?.getAttribute("content") || "";
    if (tag && description) tag.setAttribute("content", description);
    return () => {
      document.title = previousTitle;
      if (tag) tag.setAttribute("content", previous);
    };
  }, [title, description]);
}

const DynamicPage = ({ slug }) => {
  const [status, setStatus] = useState("loading");
  const [page, setPage] = useState(null);

  useEffect(() => {
    let cancel = false;
    setStatus("loading");
    setPage(null);
    fetchPublicPage(slug).then((next) => {
      if (cancel) return;
      setPage(next);
      setStatus(next ? "ready" : "missing");
    });
    return () => {
      cancel = true;
    };
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const title = page
    ? `${page.title} · SMYM Chennai`
    : status === "missing"
      ? "Page not found · SMYM Chennai"
      : "SMYM Chennai";
  usePageMeta(title, page?.description || "");

  if (status === "loading") {
    return (
      <main className="smym-page mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        <p className="text-[var(--smym-ink-muted)]">Loading page…</p>
      </main>
    );
  }

  if (!page) {
    return (
      <main className="smym-page mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        <h1 className="smym-display text-4xl text-[var(--smym-ink)]">Page not found</h1>
        <p className="mt-4 text-[var(--smym-ink-muted)]">This page is not published.</p>
        <p className="mt-6">
          <a href="/" className="smym-btn smym-btn-primary">
            Back to home
          </a>
        </p>
      </main>
    );
  }

  return (
    <main className="smym-page mx-auto max-w-3xl px-4 pb-16 sm:px-6">
      {page.coverImage ? (
        <img
          src={page.coverImage}
          alt=""
          loading="lazy"
          className="mb-6 aspect-[16/8] w-full rounded-lg object-cover"
        />
      ) : null}
      <h1 className="smym-display text-4xl text-[var(--smym-ink)] sm:text-5xl">
        {page.title}
      </h1>
      {page.description ? (
        <p className="smym-lead mt-4 max-w-none">{page.description}</p>
      ) : null}
      <PageBlocks blocks={page.blocks} />
    </main>
  );
};

export default DynamicPage;
